import { apiClient } from "@/lib/apiClient";

export interface Transcript {
  id: string;
  sessionId: string;
  createdAt: string;
  updatedAt: string;
  type: "text" | "audio" | "tool";
  content: string;
  source: "user" | "system" | "model";
  session: {
    id: string;
    companyId: string;
    type: "call" | "web_voice" | "web_chat";
    status: "active" | "completed" | "cancelled";
    company: {
      id: string;
      name: string;
    };
    user?: {
      id: string;
      firstName: string;
      lastName: string;
      email: string;
    };
    fromNumber?: string;
    toNumber?: {
      id: string;
      e164Number: string;
      friendlyName: string;
    };
    sessionTools: SessionTool[];
  };
}
export interface SessionTool {
  id: string;
  sessionId: string;
  name: string;
  arguments: any;
  result: any;
}

export interface CreateTranscriptRequest {
  sessionId: string;
  type: "text" | "audio" | "tool";
  content: string;
  source: "user" | "system" | "model";
}

export interface UpdateTranscriptRequest {
  type?: "text" | "audio" | "tool";
  content?: string;
  source?: "user" | "system" | "model";
}

class TranscriptApiService {
  // Get all transcripts with optional query parameters
  async getTranscripts(
    queryParams?: Record<string, string>
  ): Promise<Transcript[]> {
    const params = new URLSearchParams(queryParams);
    const endpoint = `/v1/transcripts${
      params.toString() ? `?${params.toString()}` : ""
    }`;
    return apiClient.get<Transcript[]>(endpoint);
  }

  // Get transcripts for a specific session
  async getTranscriptsBySession(sessionId: string): Promise<Transcript[]> {
    return apiClient.get<Transcript[]>(`/v1/sessions/${sessionId}/transcripts`);
  }

  // Get a single transcript by ID
  async getTranscript(id: string): Promise<Transcript> {
    return apiClient.get<Transcript>(`/v1/transcripts/${id}`);
  }

  // Create a new transcript
  async createTranscript(data: CreateTranscriptRequest): Promise<Transcript> {
    return apiClient.post<Transcript>(`/v1/transcripts`, { data });
  }

  // Update a transcript
  async updateTranscript(
    id: string,
    data: UpdateTranscriptRequest
  ): Promise<Transcript> {
    return apiClient.put<Transcript>(`/v1/transcripts/${id}`, { data });
  }

  // Delete a transcript
  async deleteTranscript(id: string): Promise<void> {
    return apiClient.delete<void>(`/v1/transcripts/${id}`);
  }
}

export const transcriptApi = new TranscriptApiService();
